// =================================================================
// useDevice hook
//
// Manages the device-bundled tablet "session" — a deviceId stored in
// localStorage that maps to a /brain_health/devices/{deviceId}/ record.
//
// Schema (snake_case, flat, mirroring /accounts/ and /users/ patterns):
//
//   /brain_health/devices/{deviceId}/
//     display_name              (pre-filled at fulfillment or set on first-run)
//     family_contact?           (optional email for monthly summary)
//     first_run_completed       (boolean, set after user confirms first-run form)
//     created_at
//     last_active
//     entitlement/              (server-only writes — fulfillment script)
//       type                    ('perpetual')
//       sku
//       activated_at
//     assessment/, goals/, sessions/ (mirror /accounts/ and /users/)
//
// The deviceId arrives one of two ways on a Hexnode-provisioned tablet:
//   1. First launch: the Hexnode-configured launcher URL includes
//      ?did=QLT-XXXX, which we read once and persist to localStorage.
//      We then strip the param from the URL so subsequent navigation
//      doesn't carry it around or risk leaking via share/bookmark.
//   2. Subsequent launches: read from localStorage. The device never
//      sees a sign-in screen again.
//
// Phase 3 work — not yet wired up:
//   - /brain_health/devices/ doesn't have rules yet (deferred to
//     Phase 3 when device-bundled flow is built end-to-end)
//   - Pre-activation script that writes the entitlement at fulfillment
//   - First-run form on the tablet
//
// Phase 1 ships this provider as scaffolding. It returns null/loading
// states until devices/ data exists, which won't be until Phase 3.
//
// This is intentionally separate from useResident and useAuth:
//   - useResident — community resident, login-code session
//   - useAuth — Firebase Auth user (admin OR individual subscriber)
//   - useDevice — physical tablet, pre-activated at fulfillment
//
// All three feed into useCurrentContext.
// =================================================================

import {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from './firebase.js';

const DeviceContext = createContext(null);
const STORAGE_KEY = 'brainHealth.deviceId';
const URL_PARAM = 'did';

// Read the deviceId synchronously on mount. Priority:
//   1. ?did= URL param (first launch from Hexnode-provisioned URL)
//   2. localStorage (every launch after the first)
// If we find one in the URL, persist it and strip it from the URL.
function readInitialDeviceId() {
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get(URL_PARAM);
    if (fromUrl) {
      try {
        localStorage.setItem(STORAGE_KEY, fromUrl);
      } catch {
        // localStorage unavailable (private browsing, etc.) — we'll
        // still return the deviceId for this session, just won't
        // persist. Tablet kiosk mode shouldn't hit this case.
      }
      url.searchParams.delete(URL_PARAM);
      const newSearch = url.searchParams.toString();
      window.history.replaceState(
        {},
        '',
        url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash
      );
      return fromUrl;
    }
  } catch {
    // URL parse failed — fall through to localStorage.
  }
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function DeviceProvider({ children }) {
  // deviceId is captured once on mount — Hexnode won't change it
  // mid-session, so no need to keep watching the URL.
  const [deviceId] = useState(readInitialDeviceId);

  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(!!deviceId);

  // Subscribe to the device record so admin/server-side changes
  // (entitlement updates, profile pre-fill at fulfillment) propagate
  // live to the running tablet.
  useEffect(() => {
    if (!deviceId) {
      setDevice(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const deviceRef = ref(db, `brain_health/devices/${deviceId}`);
    const unsubscribe = onValue(
      deviceRef,
      (snap) => {
        const val = snap.val();
        if (val) {
          setDevice({ id: deviceId, ...val });
        } else {
          // deviceId in localStorage but no matching record in the
          // database. Don't clear — could be a fulfillment-in-progress
          // race or a temporary network/permission blip. Leave device
          // null so the first-run UI shows a "this tablet isn't
          // registered" recovery screen instead of silently bouncing.
          setDevice(null);
        }
        setLoading(false);
      },
      (err) => {
        // Permission denied expected on read until Phase 3 rules
        // are in place. Suppress that specific error.
        if (err.code !== 'PERMISSION_DENIED') {
          console.error('Device record read failed:', err);
        }
        setDevice(null);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [deviceId]);

  // Entitlement check. For Phase 1/2/3 we only support perpetual
  // entitlements on devices (the device-bundled SKU). Future shapes
  // (rented tablet, expiring trial) would add more types here without
  // changing the surface that callers read.
  const hasEntitlement =
    !!device?.entitlement && device.entitlement.type === 'perpetual';

  // First-run flag. The fulfillment script may pre-write display_name
  // from the buyer's order, but first_run_completed is set only after
  // the resident confirms (or fills in) their name on the tablet.
  const needsFirstRun = !!device && !device.first_run_completed;

  const value = {
    deviceId,
    device,
    loading,
    // Has the URL/localStorage given us a deviceId at all? (Pre-RTDB
    // check — useful for routing: a tablet with no deviceId at all is
    // probably someone visiting the public site on a random browser.)
    hasDeviceId: !!deviceId,
    // Has the deviceId resolved to a real, entitled device record?
    isActiveDevice: !!device,
    hasEntitlement,
    needsFirstRun,
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
}

export function useDevice() {
  const ctx = useContext(DeviceContext);
  if (!ctx) {
    throw new Error('useDevice must be used inside <DeviceProvider>');
  }
  return ctx;
}
